import { useWalletState } from '../hooks/use-wallet-state';
import { useTransactions } from '../hooks/use-transactions';
import { MetricCard } from '@/components/organisms/cards';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/atoms/card';
import { Alert, AlertDescription } from '@/components/atoms/alert';
import { Badge } from '@/components/atoms/badge';
import { Button } from '@/components/atoms/button';
import { Skeleton } from '@/components/atoms/skeleton';
import { useTranslation } from 'react-i18next';
import { Wallet, ArrowUpRight, ArrowDownLeft, Clock, DollarSign, AlertCircle, Lock } from 'lucide-react';
import { formatPrice, formatDate, convertFromEur } from '@/lib/utils';
import { useConfigStore } from '@/lib/stores/config.store';
import { Link } from 'react-router-dom';
import { WalletOnboarding } from '../components/wallet-onboarding';
import { PayoutOnboardingFlow } from '@/features/payouts/components/payout-onboarding-flow';
import { useState } from 'react';
import type { TransactionType, TransactionStatus } from '../types/wallet.types';

const statusVariantMap: Record<TransactionStatus, 'default' | 'secondary' | 'outline' | 'destructive'> = {
  pending: 'outline',
  completed: 'default',
  failed: 'destructive',
  cancelled: 'secondary',
};

const typeIconMap: Record<TransactionType, React.ElementType> = {
  sale: ArrowDownLeft,
  refund: ArrowUpRight,
  withdrawal: ArrowUpRight,
  fee: DollarSign,
  adjustment: DollarSign,
};

export function WalletContainer() {
  const { t } = useTranslation();
  const { currency } = useConfigStore();
  const { data: wallet, isLoading: walletLoading } = useWalletState();
  const { data: transactions, isLoading: transactionsLoading } = useTransactions();
  // Doc 51 Phase 5 — le parcours (Popup 1 PSP → Popup 2 « déjà un compte ? » /
  // formulaire destination + MFA) est piloté par `PayoutOnboardingFlow`.
  const [showOnboardingFlow, setShowOnboardingFlow] = useState(false);

  // Show loading state
  if (walletLoading) {
    return (
      <div className="space-y-6">
        <div className="grid gap-4 md:grid-cols-2 lg:grid-cols-3">
          {[1, 2, 3].map((i) => (
            <Card key={i} className="gap-4 py-4">
              <div className="px-4 space-y-2">
                <Skeleton className="h-4 w-24" />
                <Skeleton className="h-8 w-32" />
              </div>
            </Card>
          ))}
        </div>
      </div>
    );
  }

  // NO_ACCOUNT state: seller hasn't created a payout account yet
  if (!wallet?.hasAccount) {
    return (
      <>
        <WalletOnboarding onStartOnboarding={() => setShowOnboardingFlow(true)} />
        <PayoutOnboardingFlow open={showOnboardingFlow} onOpenChange={setShowOnboardingFlow} />
      </>
    );
  }

  const balance = wallet.balance;
  const hasReserved = typeof balance?.reserved === 'number' && balance.reserved > 0;
  // `balance.total` (SellerBalance.total, pay-v2) n'égale available + pending
  // + reserved QUE quand le plancher `available = max(0, mature - reserved)`
  // ne joue pas (seller-balance.service.ts) : dès que reserved > mature,
  // available tombe à 0 sans que total ne suive, et un re-sum local
  // désaccorderait avec le ledger. C'est pour ça qu'on consomme `total` du
  // serveur tel quel plutôt que de le recalculer. Le repli local ne
  // s'applique qu'au solde-zéro (où `total` est absent, pas de compte
  // seller — `reserved` y vaut aussi 0, donc le plancher ne s'y manifeste
  // jamais) — il inclut `reserved` pour ne plus faire disparaître
  // silencieusement l'argent gelé par un retrait en cours (Doc 55, Task B1 :
  // c'était l'écart entre pay-saas 60 € et web-pro 30 €).
  const totalBalance =
    balance?.total ?? (balance?.available ?? 0) + (balance?.pending ?? 0) + (balance?.reserved ?? 0);

  return (
    <div className="space-y-6">
      {/* Balance Cards */}
      <div className="grid gap-4 md:grid-cols-2 lg:grid-cols-4">
        <MetricCard
          title={t('wallet.available')}
          value={formatPrice(convertFromEur(balance?.available ?? 0, currency), currency)}
          icon={Wallet}
        />
        <MetricCard
          title={t('wallet.pending')}
          value={formatPrice(convertFromEur(balance?.pending ?? 0, currency), currency)}
          icon={Clock}
        />
        {hasReserved && (
          <MetricCard
            title={t('wallet.reserved')}
            value={formatPrice(convertFromEur(balance!.reserved!, currency), currency)}
            description={t('wallet.reservedExplanation')}
            icon={Lock}
          />
        )}
        <MetricCard
          title={t('wallet.total')}
          value={formatPrice(convertFromEur(totalBalance, currency), currency)}
          icon={DollarSign}
        />
      </div>

      {/* Contextual banner based on destination status */}
      {wallet.destinationStatus === 'NONE' && (
        <Alert>
          <AlertCircle className="size-4" />
          <AlertDescription>
            {t('wallet.banner.addMethod', 'Ajoutez un moyen de versement pour retirer vos gains.')}
          </AlertDescription>
        </Alert>
      )}
      {wallet.destinationStatus === 'PENDING_VERIFICATION' && (
        <Alert>
          <Clock className="size-4" />
          <AlertDescription>
            {t('wallet.banner.pending', 'Votre moyen de versement est en cours de vérification.')}
          </AlertDescription>
        </Alert>
      )}

      {/* Actions */}
      <div className="flex gap-4">
        {wallet.canWithdraw ? (
          <Link to="/wallet/payouts">
            <Button>
              <ArrowUpRight className="size-4 mr-2" />
              {t('wallet.withdraw')}
            </Button>
          </Link>
        ) : wallet.destinationStatus !== 'VERIFIED' ? (
          <Link to="/wallet/payouts">
            <Button>
              <ArrowUpRight className="size-4 mr-2" />
              {t('wallet.addMethod', 'Ajouter un moyen de versement')}
            </Button>
          </Link>
        ) : (
          <Button disabled>
            <ArrowUpRight className="size-4 mr-2" />
            {t('wallet.noBalance', 'Aucun solde disponible')}
          </Button>
        )}
      </div>

      {/* Recent Transactions */}
      <Card>
        <CardHeader className="flex flex-row items-center justify-between">
          <CardTitle>{t('wallet.recentTransactions')}</CardTitle>
          <Link to="/wallet/transactions">
            <Button variant="outline" size="sm">
              {t('wallet.viewAll')}
            </Button>
          </Link>
        </CardHeader>
        <CardContent>
          {transactionsLoading ? (
            <div className="space-y-4">
              {[1, 2, 3, 4, 5].map((i) => (
                <Skeleton key={i} className="h-16 w-full" />
              ))}
            </div>
          ) : transactions && transactions.length > 0 ? (
            <div className="space-y-4">
              {transactions.slice(0, 10).map((transaction, index) => {
                const TypeIcon = typeIconMap[transaction.type] || DollarSign;
                const isIncoming = transaction.type === 'sale';
                return (
                  <div
                    key={transaction.id || `transaction-${index}`}
                    className="flex items-center justify-between py-3 border-b last:border-0"
                  >
                    <div className="flex items-center gap-4">
                      <div
                        className={`rounded-full p-2 ${
                          isIncoming ? 'bg-green-100 text-green-600' : 'bg-red-100 text-red-600'
                        }`}
                      >
                        <TypeIcon className="size-4" />
                      </div>
                      <div>
                        <p className="font-medium">{transaction.description}</p>
                        <p className="text-sm text-muted-foreground">
                          {formatDate(transaction.createdAt)}
                        </p>
                        {transaction.orderRefId && (
                          <Link to={`/orders/${transaction.orderId}`} className="text-xs text-primary hover:underline">
                            #{transaction.orderRefId}
                          </Link>
                        )}
                      </div>
                    </div>
                    <div className="text-right">
                      <p
                        className={`font-bold ${
                          isIncoming ? 'text-green-600' : 'text-red-600'
                        }`}
                      >
                        {isIncoming ? '+' : '-'}
                        {formatPrice(convertFromEur(Math.abs(transaction.netAmount), currency), currency)}
                      </p>
                      {transaction.fee !== undefined && transaction.fee > 0 && (
                        <p className="text-xs text-muted-foreground">
                          {t('wallet.fee')}: {formatPrice(convertFromEur(transaction.fee, currency), currency)}
                        </p>
                      )}
                      <Badge variant={statusVariantMap[transaction.status]} className="text-xs">
                        {t(`wallet.status.${transaction.status}`)}
                      </Badge>
                    </div>
                  </div>
                );
              })}
            </div>
          ) : (
            <div className="flex items-center justify-center h-32 text-muted-foreground">
              {t('wallet.noTransactions')}
            </div>
          )}
        </CardContent>
      </Card>
    </div>
  );
}
